import { Button, Stack } from '@mui/material';
import { toast } from '@side/pantry';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { InteractionType } from '@/common/types/interaction';
import {
  getListingMatchKey,
  useMatchingListings,
} from '@/components/AddressInput/StreetAutocomplete/useMatchingListings';
import { useLicensedStateContext } from '@/contexts/LicensedStateContext';
import { AddressForm } from '@/views/NewInteraction/AddressForm';
import { CancelButton } from '@/views/NewInteraction/CancelButton';
import { useReferralStateOverrides } from '@/views/NewInteraction/hooks/useReferralStateOverrides/useReferralStateOverrides';
import {
  TrackInteractionsEvent,
  useTrackInteractionsAnalytics,
} from '$analytics/useTrackAnalytics/domains/interactions';
import { TrackOfferEvent } from '$analytics/useTrackAnalytics/domains/offers/events';
import { useTrackOfferAnalytics } from '$analytics/useTrackAnalytics/domains/offers/useTrackOfferAnalytics';
import { useAppFlags } from '$launch-darkly/app-flags';
import { useCreateOfferContext } from '../context/CreateOfferContext';
import { useBuyerStateOverrides } from '../hooks/useBuyerStateOverrides';
import { useUpdateStep } from '../hooks/useUpdateStep';
import type { CreateOfferFormData } from '../types';
import { ListingMatchDialog } from './ListingMatchDialog';

export const AddressStep = () => {
  const updateStep = useUpdateStep();
  const { updateContext, sourceId } = useCreateOfferContext();
  const stateOverridesFromReferral = useReferralStateOverrides();
  const stateOverridesFromBuyer = useBuyerStateOverrides();
  const stateOverrides = useMemo(() => {
    if (stateOverridesFromBuyer) {
      return stateOverridesFromBuyer;
    }
    return stateOverridesFromReferral;
  }, [stateOverridesFromBuyer, stateOverridesFromReferral]);

  const trackInteractionsEvent = useTrackInteractionsAnalytics();
  const trackOfferEvent = useTrackOfferAnalytics();

  useEffect(() => {
    trackInteractionsEvent(TrackInteractionsEvent.EnterAddressDecouplingModal, {
      eventContainer: 'offer_address_card',
    });
  }, [trackInteractionsEvent]);

  const {
    getValues,
    setValue,
    formState: { isValid },
  } = useFormContext<CreateOfferFormData>();

  const { txmLinkOffersListings } = useAppFlags();
  const street1 = useWatch<CreateOfferFormData, 'street1'>({ name: 'street1' });
  const listingId = useWatch<CreateOfferFormData, 'listingId'>({
    name: 'listingId',
  });
  const matchEnabled = !!txmLinkOffersListings && !listingId;
  const listingMatchMap = useMatchingListings(street1, matchEnabled);
  // Both the typed-address match (on Next) and the MLS-autofill match open the
  // same dialog. They share "Use Listing" (link + advance) but differ on
  // reject: Next advances unlinked, MLS autofill runs the normal autofill.
  const [listingMatch, setListingMatch] = useState<{
    listingId: string;
    trigger: 'mls_autofill' | 'stepper_next';
    onReject: () => void;
  }>();

  useEffect(() => {
    if (listingMatch) {
      trackOfferEvent(TrackOfferEvent.ListingMatchModalMount, {
        listingId: listingMatch.listingId,
        matchTrigger: listingMatch.trigger,
      });
    }
  }, [listingMatch, trackOfferEvent]);

  const { stateError } = useLicensedStateContext();

  useEffect(() => {
    if (stateError) {
      toast('error', stateError, {
        showCloseButton: true,
      });
    }
  }, [stateError]);

  return (
    <>
      <AddressForm
        type={InteractionType.offer}
        enableMlsAutofill
        enableListingMatch
        sourceId={sourceId}
        handleSetErrorMessage={(error) =>
          updateContext({ errorMessage: error })
        }
        headerText="Tell us about the property being purchased"
        subheaderText="If the address is autofilled from the MLS, please review the address to make sure it’s complete and correct"
        onListingMatch={
          matchEnabled
            ? (matchedListingId, runAutofill) =>
                setListingMatch({
                  listingId: matchedListingId,
                  trigger: 'mls_autofill',
                  onReject: () => void runAutofill(),
                })
            : undefined
        }
        stateOptions={stateOverrides}
        AlertProps={
          stateOverridesFromBuyer
            ? {
                severity: 'info',
                children:
                  'State selection is restricted to the states covered by the licenses of the linked buyer agents',
              }
            : undefined
        }
      />
      <Stack
        sx={{
          gap: 2,
          flexDirection: { xs: 'column', lg: 'row' },
          justifyContent: { xs: 'center', lg: 'flex-end' },
        }}
      >
        <CancelButton type={InteractionType.offer} />
        <Button
          size="large"
          variant="contained"
          disabled={stateError.length > 0 || !isValid}
          onClick={() => {
            trackOfferEvent(TrackOfferEvent.ClickCreateOfferNext);
            if (matchEnabled) {
              const { street1, city, state } = getValues();
              const match = listingMatchMap.get(
                getListingMatchKey({ street1, city, state }),
              );
              if (match) {
                setListingMatch({
                  listingId: match,
                  trigger: 'stepper_next',
                  onReject: () => updateStep(1),
                });
                return;
              }
            }
            updateStep(1);
          }}
        >
          Next
        </Button>
      </Stack>
      <ListingMatchDialog
        listingId={listingMatch?.listingId}
        onUseListing={(state) => {
          trackOfferEvent(TrackOfferEvent.ClickUseListing, {
            listingId: listingMatch?.listingId,
          });
          updateContext({ linkSource: 'modal' });
          setValue('listingId', listingMatch?.listingId, {
            shouldDirty: true,
          });
          // State is needed for the client group searches on step 2
          if (state) {
            setValue('state', state, { shouldDirty: true });
          }
          setListingMatch(undefined);
          updateStep(1);
        }}
        onWrongListing={() => {
          trackOfferEvent(TrackOfferEvent.DismissListingMatchModal, {
            listingId: listingMatch?.listingId,
            matchTrigger: listingMatch?.trigger,
            eventTarget: 'wrong_listing_button',
          });
          const { onReject } = listingMatch ?? {};
          setListingMatch(undefined);
          onReject?.();
        }}
        onClose={() => {
          trackOfferEvent(TrackOfferEvent.DismissListingMatchModal, {
            listingId: listingMatch?.listingId,
            matchTrigger: listingMatch?.trigger,
          });
          setListingMatch(undefined);
        }}
      />
    </>
  );
};
